import { Message, Product, User } from "module/types";
import UserListModal, { UserListModalUser } from "./common/UserListModal";
import SocketProtocol from "network/SocketProtocol";
import SocketService from "network/SocketService";
import UserSession from "network/UserSession";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import "sass/components/Chatbox.scss";

type ChatboxProps = {
  product: Product;
  roomName: string;
  userList: User[] | null;
  chatList: Message[];
  smallChatKey: string;
  isUserListOpen: boolean;
  // fullSize: boolean;
  setChatList: Dispatch<SetStateAction<Message[]>>;
  setIsUserListOpen: Dispatch<SetStateAction<boolean>>;
};

function Chatbox({
  product,
  roomName,
  userList,
  chatList,
  smallChatKey,
  isUserListOpen,
  // fullSize,
  setChatList,
  setIsUserListOpen,
}: ChatboxProps) {
  const { t } = useTranslation();
  const chatRef = useRef<HTMLDivElement>(null);
  // const [showUserList, setShowUserList] = useState(false);
  const [message, setMessage] = useState("");

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && message.trim() !== "") {
      event.preventDefault();
      if (event.nativeEvent.isComposing) {
        sendChat();
      }
    }
  };

  const sendChat = () => {
    if (message.trim() !== "") {
      const chatMessage = {
        user: UserSession.getInstance().getUserNickName(),
        userId: UserSession.getInstance().getUserId(),
        content: message,
        type: "message",
        smallChatKey: smallChatKey,
      };

      SocketService.getInstance().emit(
        SocketProtocol.CHAT,
        product.id,
        roomName,
        chatMessage
      );
      setMessage("");
    }
  };

  // Process and filter messages for display
  const displayMessages = React.useMemo(() => {
    return chatList
      .filter((message) => {
        // Filter messages based on smallChatKey
        if (
          message.smallChatKey !== undefined &&
          message.smallChatKey !== smallChatKey
        ) {
          return false;
        }

        // Skip join/leave messages if no userList context
        if (message.type === "join" || message.type === "leave") {
          if (userList === null) {
            return false;
          }
        }

        return true;
      })
      .map((message) => {
        // Transform join/leave messages
        if (message.type === "join" || message.type === "leave") {
          const transformedMessage = {
            ...message,
            content:
              message.type === "join" ? t("joinMessage") : t("leaveMessage"),
          };

          return transformedMessage;
        }

        return message;
      });
  }, [chatList, smallChatKey, userList, t]);

  useEffect(() => {
    //scroll To bottom
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [displayMessages]);

  return (
    <div className="chat-container">
      <div className="chat-body">
        <div className="chat-box" ref={chatRef}>
          {displayMessages.map((message, index) => (
            <div className="message-container mb-1" key={`${message.smallChatKey || 'public'}-${message.index ?? index}-${message.userId}-${index}`}>
              {message.type === "join" || message.type === "leave" ? (
                <div
                  className={`join-message-wrapper ${
                    message.type === "join" ? "bg-green-50" : "bg-red-50"
                  } p-2 rounded flex items-center m-1`}
                >
                  <i
                    className={`mr-2 ${
                      message.type === "join"
                        ? "fas fa-sign-in-alt text-green-500"
                        : "fas fa-sign-out-alt text-red-500 fa-rotate-180"
                    }`}
                  />
                  <span className="join-message text-sm">
                    <strong>{message.user}</strong>
                    {message.content}
                  </span>
                </div>
              ) : message.userId === UserSession.getInstance().getUserId() ? (
                /* self message */
                <div className="ml-auto bg-indigo-600 text-white px-3 py-2 rounded-2xl rounded-tr-sm max-w-[75%] break-words shadow-md">
                  <div className="text-xs">{message.content}</div>
                </div>
              ) : (
                <div className="flex">
                  <div className="mr-2 flex-shrink-0 mt-px">
                    <i className="fa-solid fa-circle-user fa-2xl text-green-200"></i>
                    {/* <div className="w-8 h-8 rounded-full bg-gray-700 overflow-hidden">
                      {users.find((u) => u.name === msg.user)?.avatar && (
                        <img
                          src={users.find((u) => u.name === msg.user)?.avatar}
                          alt={msg.user}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div> */}
                  </div>
                  <div>
                    <div className="bg-gray-700 text-white px-3 py-2 rounded-2xl rounded-tl-sm max-w-md shadow-md">
                      <div className="font-medium text-indigo-300 mb-1 text-xs">
                        {message.user}
                      </div>
                      <div className="text-xs">{message.content}</div>
                    </div>
                  </div>
                </div>

                // <div className="user-message-wrapper">
                //   <p className="user-message">
                //     <strong>{message.user} </strong>
                //     {message.content}
                //   </p>
                // </div>
              )}
            </div>
          ))}
        </div>
        {/* input area */}
        <div className="flex-0.5 border-t border-gray-700 p-2 bg-gray-800">
          <div className="flex gap-2">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("typeMessage")}
              className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 border-none text-xs resize-none"
              rows={1}
            />
            <button
              onClick={sendChat}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg whitespace-nowrap cursor-pointer transition-colors duration-200 flex items-center justify-center"
            >
              <i className="fas fa-paper-plane text-xs"></i>
            </button>
          </div>
        </div>
      </div>
      {userList && (
        <UserListModal
          userList={userList.map(
            (user): UserListModalUser => ({
              socketId: user.socketId,
              defaultNickName: user.defaultNickName,
              nickName: user.nickName,
              id: user.id,
              isHost: user.isHost,
              state: user.state,
              isAI: user.isAI,
            })
          )}
          isOpen={isUserListOpen}
          onClose={() => setIsUserListOpen(false)}
          variant="embedded"
        />
      )}
    </div>
  );
}

Chatbox.defaultProps = {
  userList: null,
  smallChatKey: "",
  isUserListOpen: false,
  setIsUserListOpen: null,
};

export default Chatbox;
